/**
 * User profile page for /profile route.
 * Allows users to view and edit their own profile.
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import {
  updateUserProfile,
  updateUserTimezone,
  updateUserLocale,
} from "../services/naraDoor";
import {
  User,
  ArrowLeft,
  Pencil,
  Save,
  X,
  Mail,
  Phone,
  Globe,
  Clock,
  CreditCard,
  ChevronRight,
  Zap,
} from "lucide-react";
import { usePayment } from "../lib/PaymentContext";
import { publicRoutes } from "../lib/routes";

export function UserProfile() {
  const { t } = useTranslation();
  const { profile, isLoading, refreshProfile } = useProfile();
  const { subscriptionTier, boostBalance, loading: paymentLoading, billingHidden } = usePayment();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editLocale, setEditLocale] = useState("");
  const [editTimezone, setEditTimezone] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setEditFirstName(profile.firstName || "");
      setEditLastName(profile.lastName || "");
      setEditLocale(profile.locale || "");
      setEditTimezone(profile.timezone || "");
    }
  }, [profile]);

  const profileMutation = useMutation({
    mutationFn: (data: { firstName?: string; lastName?: string }) =>
      updateUserProfile(data),
    onSuccess: () => {
      refreshProfile();
    },
  });

  const timezoneMutation = useMutation({
    mutationFn: (timezone: string) => updateUserTimezone(timezone),
    onSuccess: () => {
      refreshProfile();
    },
  });

  const localeMutation = useMutation({
    mutationFn: (locale: string) => updateUserLocale(locale),
    onSuccess: () => {
      refreshProfile();
    },
  });

  const handleStartEdit = () => {
    setEditFirstName(profile?.firstName || "");
    setEditLastName(profile?.lastName || "");
    setEditLocale(profile?.locale || "");
    setEditTimezone(profile?.timezone || "");
    setSaveError(null);
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaveError(null);

    try {
      // Only name is editable here. Email and phone are identity fields that
      // require proving control of the new value — changed via support.
      if (
        editFirstName !== (profile?.firstName || "") ||
        editLastName !== (profile?.lastName || "")
      ) {
        await profileMutation.mutateAsync({
          firstName: editFirstName || undefined,
          lastName: editLastName || undefined,
        });
      }

      // Update locale if changed
      if (editLocale && editLocale !== profile?.locale) {
        await localeMutation.mutateAsync(editLocale);
      }

      // Update timezone if changed
      if (editTimezone && editTimezone !== profile?.timezone) {
        await timezoneMutation.mutateAsync(editTimezone);
      }

      setIsEditing(false);
    } catch (err) {
      setSaveError((err as Error).message);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  const isSaving =
    profileMutation.isPending ||
    timezoneMutation.isPending ||
    localeMutation.isPending;

  const getUserDisplayName = () => {
    if (profile?.firstName || profile?.lastName) {
      return `${profile.firstName || ""} ${profile.lastName || ""}`.trim();
    }
    return profile?.email || "User";
  };

  return (
    <div>
      <PageHeader
        title={t("profile.title")}
        description={t("profile.description")}
        breadcrumbs={[
          { label: t("nav.dashboard"), href: publicRoutes.home },
          { label: t("nav.profile") },
        ]}
        actions={
          <Link
            to={publicRoutes.home}
            className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("profile.back")}
          </Link>
        }
      />

      {/* Loading state */}
      {isLoading ? (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-muted animate-pulse" />
            <div className="space-y-2">
              <div className="h-6 w-40 bg-muted rounded animate-pulse" />
              <div className="h-4 w-56 bg-muted rounded animate-pulse" />
            </div>
          </div>
          <div className="h-64 bg-muted rounded-lg animate-pulse" />
        </div>
      ) : null}

      {/* Profile details */}
      {!isLoading && profile && (
        <>
        <div className="bg-card rounded-lg border border-border">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                {profile.avatarUrl ? (
                  <img
                    src={profile.avatarUrl}
                    alt=""
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  <User className="h-8 w-8 text-blue-600" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {getUserDisplayName()}
                </h2>
                <p className="text-sm text-muted-foreground">{profile.email}</p>
              </div>
            </div>
            {!isEditing && (
              <button
                onClick={handleStartEdit}
                className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors"
              >
                <Pencil className="h-4 w-4" />
                {t("profile.edit")}
              </button>
            )}
          </div>

          <div className="p-6">
            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t("profile.firstName")}
                    </label>
                    <input
                      type="text"
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t("profile.lastName")}
                    </label>
                    <input
                      type="text"
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("profile.email")}
                  </label>
                  <p className="text-foreground">{profile.email || "—"}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("profile.contactSupportToChange")}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("profile.phone")}
                  </label>
                  <p className="text-foreground">{profile.phone || "—"}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("profile.contactSupportToChange")}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t("profile.locale")}
                    </label>
                    <select
                      value={editLocale}
                      onChange={(e) => setEditLocale(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{t("profile.selectLocale")}</option>
                      <option value="en-CA">{t("profile.locales.enCA")}</option>
                      <option value="en-US">{t("profile.locales.enUS")}</option>
                      <option value="fr-CA">{t("profile.locales.frCA")}</option>
                      <option value="es-US">{t("profile.locales.esUS")}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t("profile.timezone")}
                    </label>
                    <select
                      value={editTimezone}
                      onChange={(e) => setEditTimezone(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{t("profile.selectTimezone")}</option>
                      <optgroup label={t("profile.timezones.canada")}>
                        <option value="America/Toronto">{t("profile.timezones.toronto")}</option>
                        <option value="America/Montreal">{t("profile.timezones.montreal")}</option>
                        <option value="America/Halifax">{t("profile.timezones.halifax")}</option>
                        <option value="America/Winnipeg">{t("profile.timezones.winnipeg")}</option>
                        <option value="America/Regina">{t("profile.timezones.regina")}</option>
                        <option value="America/Edmonton">{t("profile.timezones.edmonton")}</option>
                        <option value="America/Vancouver">{t("profile.timezones.vancouver")}</option>
                        <option value="America/St_Johns">{t("profile.timezones.stJohns")}</option>
                      </optgroup>
                      <optgroup label={t("profile.timezones.unitedStates")}>
                        <option value="America/New_York">{t("profile.timezones.newYork")}</option>
                        <option value="America/Chicago">{t("profile.timezones.chicago")}</option>
                        <option value="America/Denver">{t("profile.timezones.denver")}</option>
                        <option value="America/Phoenix">{t("profile.timezones.phoenix")}</option>
                        <option value="America/Los_Angeles">{t("profile.timezones.losAngeles")}</option>
                        <option value="America/Anchorage">{t("profile.timezones.anchorage")}</option>
                        <option value="Pacific/Honolulu">{t("profile.timezones.honolulu")}</option>
                      </optgroup>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-4">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    {isSaving ? t("profile.saving") : t("profile.save")}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="inline-flex items-center gap-2 px-4 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
                  >
                    <X className="h-4 w-4" />
                    {t("profile.cancel")}
                  </button>
                </div>
                {saveError && (
                  <p className="text-sm text-red-600">{saveError}</p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t("profile.firstName")}</h3>
                    <p className="text-foreground">{profile.firstName || "—"}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t("profile.lastName")}</h3>
                    <p className="text-foreground">{profile.lastName || "—"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-foreground">{profile.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-foreground">{profile.phone || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <span className="text-foreground">{profile.locale || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-foreground">{profile.timezone || "—"}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Billing & Subscription Section — hidden for a contract workspace */}
        {!billingHidden && (
          <div className="mt-6 bg-card rounded-lg border border-border">
            <div className="px-6 py-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    {t("profile.billingTitle", "Billing & Subscription")}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {t("profile.billingDescription", "Manage your subscription and payment methods")}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Current Plan & Balance Summary */}
              {!paymentLoading && (
                <div className="flex items-center gap-6 pb-4 border-b border-border">
                  <div>
                    <p className="text-sm text-muted-foreground">{t("profile.currentPlan", "Current Plan")}</p>
                    <p className="text-lg font-medium text-foreground capitalize">{subscriptionTier}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t("profile.boostBalance", "Boost Balance")}</p>
                    <p className="text-lg font-medium text-foreground flex items-center gap-1">
                      <Zap className="h-4 w-4 text-amber-500" />
                      {boostBalance}
                    </p>
                  </div>
                </div>
              )}

              {/* Billing Links */}
              <div className="space-y-2">
                <Link
                  to="/billing"
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-muted-foreground group-hover:text-foreground" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {t("profile.manageBilling", "Manage Billing")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("profile.manageBillingDesc", "Subscription, payment methods, and auto top-up")}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground" />
                </Link>

                <Link
                  to="/billing-history"
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-muted-foreground group-hover:text-foreground" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {t("profile.billingHistory", "Billing History")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("profile.billingHistoryDesc", "View past transactions and invoices")}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground" />
                </Link>

                <Link
                  to="/boosts-history"
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <Zap className="h-5 w-5 text-muted-foreground group-hover:text-foreground" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {t("profile.boostsHistory", "Boosts History")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("profile.boostsHistoryDesc", "View boost consumption and purchases")}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground" />
                </Link>
              </div>
            </div>
          </div>
        )}
        </>
      )}
    </div>
  );
}
